import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, Modal, message, Button, Space, Form, Input } from 'antd';
import OrganizationHierarchyManager from '../components/OrganizationHierarchyManager';
import api from '../api/http';

const inputStyle = {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
};

const buttonStyle = {
    padding: '8px 16px',
    border: 'none',
    borderRadius: 6,
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 14,
};

const Settings = () => {
  const [settings, setSettings] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [minioForm] = Form.useForm();
  // 已移除平铺管理：保留最小变量（如未来需要可再次加入）

  const isMinioSettingKey = useCallback((key) => {
    return typeof key === 'string' && key.startsWith('minio_');
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      const errMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      message.error(errMsg || '获取系统设置失败（请确认已登录且拥有 manage_settings 权限）');
      setSettings([]);
    }
  };
  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const safeSettings = useMemo(() => (Array.isArray(settings) ? settings : []), [settings]);
  const getSettingValue = useCallback(
    (key) => safeSettings.find(s => s.setting_key === key)?.setting_value,
    [safeSettings]
  );

  // 回填数据设置表单（MinIO）
  useEffect(() => {
    minioForm.setFieldsValue({
      minio_endpoint: getSettingValue('minio_endpoint') || '',
      minio_access_key: getSettingValue('minio_access_key') || 'minioadmin',
      minio_secret_key: getSettingValue('minio_secret_key') || 'minioadmin123456'
    });
  }, [settings, minioForm, getSettingValue]);

  const handleUpdate = async (key, value) => {
    if (isMinioSettingKey(key)) {
      message.warning('MinIO 配置请在「数据设置」中修改');
      return;
    }
    try {
      await api.put(`/settings/${encodeURIComponent(key)}`, { value });
      fetchSettings();
    } catch (error) {
      console.error('Error updating setting:', error);
      const errMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      message.error(errMsg || '更新设置失败');
    }
  };
  
  const handleAddNew = async (e) => {
    e.preventDefault();
    if (!newKey || !newValue) {
        alert('Key and value cannot be empty');
        return;
    }
    if (isMinioSettingKey(newKey)) {
      message.warning('MinIO 配置请在「数据设置」中修改');
      return;
    }
    await handleUpdate(newKey, newValue);
    setNewKey('');
    setNewValue('');
  };

  const handleValueChange = (key, value) => {
    setSettings(settings.map(s => s.setting_key === key ? {...s, setting_value: value} : s));
  };

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const handleSaveMinio = async () => {
    try {
      const values = await minioForm.validateFields();
      const normalizedEndpoint = String(values.minio_endpoint || '').trim() === '127.0.0.0'
        ? '127.0.0.1'
        : values.minio_endpoint;
      message.loading({ content: '正在验证 MinIO 连接...', key: 'minioSave' });
      await api.post('/settings/validate-minio', {
        endpoint: normalizedEndpoint,
        accessKey: values.minio_access_key,
        secretKey: values.minio_secret_key
      });
      message.loading({ content: '验证通过，正在保存...', key: 'minioSave' });
      await Promise.all([
        api.put('/settings/minio_endpoint', { value: normalizedEndpoint || '' }),
        api.put('/settings/minio_access_key', { value: values.minio_access_key || 'minioadmin' }),
        api.put('/settings/minio_secret_key', { value: values.minio_secret_key || 'minioadmin123456' })
      ]);
      message.success({ content: 'MinIO 配置已保存并验证通过', key: 'minioSave' });
      fetchSettings();
    } catch (e) {
      if (e?.errorFields) return; // antd 表单校验错误
      const data = e?.response?.data;
      let errMsg = data?.error || data?.message || e?.message;
      if (data?.attempted?.endPoint && data?.attempted?.port != null) {
        const proto = data?.attempted?.useSSL ? 'https' : 'http';
        errMsg = `${errMsg || '保存 MinIO 配置失败'}（尝试：${proto}://${data.attempted.endPoint}:${data.attempted.port}）`;
      }
      if (data?.detail?.code) {
        errMsg = `${errMsg || '保存 MinIO 配置失败'} [${data.detail.code}]`;
      }
      message.error({ content: errMsg || '保存 MinIO 配置失败', key: 'minioSave' });
    }
  };

  const generalSettingsContent = (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 16, color: '#111827' }}>主题设置</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom:24 }}>
          <span>当前主题: {theme === 'light' ? '明亮' : '暗黑'}</span>
          <button onClick={toggleTheme} style={buttonStyle}>切换主题</button>
      </div>
      <h3 style={{ marginTop: 0, marginBottom: 16, color: '#111827' }}>通用设置</h3>
        <form onSubmit={handleAddNew} style={{ marginBottom: 24, display: 'flex', gap: 16, alignItems: 'center' }}>
          <input 
              type="text" 
              placeholder="新设置项的Key" 
              value={newKey} 
              onChange={e => setNewKey(e.target.value)} 
              style={inputStyle}
          />
          <input 
              type="text" 
              placeholder="新设置项的Value" 
              value={newValue} 
              onChange={e => setNewValue(e.target.value)} 
              style={inputStyle}
          />
          <button type="submit" style={buttonStyle}>添加新设置</button>
        </form>
        <div style={{ marginTop:24 }}>
          {safeSettings
            .filter(setting => !isMinioSettingKey(setting.setting_key))
            .map(setting => (
            <div key={setting.setting_key} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <label style={{ minWidth: 150, color: '#374151' }}>{setting.setting_key}:</label>
              <input
                type="text"
                value={setting.setting_value}
                onChange={(e) => handleValueChange(setting.setting_key, e.target.value)}
                style={inputStyle}
              />
              <button onClick={() => handleUpdate(setting.setting_key, setting.setting_value)} style={buttonStyle}>
                更新
              </button>
            </div>
          ))}
        </div>
    </div>
  );

  const dataSettingsContent = (
    <div style={{ maxWidth: 720 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16, color: '#111827' }}>数据设置</h3>
      <Form
        form={minioForm}
        layout="vertical"
        initialValues={{
          minio_access_key: 'minioadmin',
          minio_secret_key: 'minioadmin123456'
        }}
      >
        <Form.Item
          label="MinIO 服务器 IP/域名"
          name="minio_endpoint"
          rules={[{ required: true, message: '请输入 MinIO 服务器 IP/域名' }]}
        >
          <Input placeholder="例如：192.168.1.10 或 minio.my-company.local" />
        </Form.Item>
        <Form.Item
          label="MinIO 用户名"
          name="minio_access_key"
          rules={[{ required: true, message: '请输入 MinIO 用户名' }]}
        >
          <Input placeholder="默认：minioadmin" />
        </Form.Item>
        <Form.Item
          label="MinIO 密码"
          name="minio_secret_key"
          rules={[{ required: true, message: '请输入 MinIO 密码' }]}
        >
          <Input.Password placeholder="默认：minioadmin123456" />
        </Form.Item>

        <Space>
          <Button type="primary" onClick={handleSaveMinio}>保存</Button>
          <Button onClick={() => minioForm.setFieldsValue({
            minio_access_key: 'minioadmin',
            minio_secret_key: 'minioadmin123456'
          })}>恢复默认账号密码</Button>
        </Space>
      </Form>
      <div style={{ marginTop: 12, color: '#6b7280' }}>
        修改后会写入 system_settings（minio_endpoint/minio_access_key/minio_secret_key）。
      </div>
    </div>
  );

  // 组织结构同步操作
  const handleOrgSync = async (mode='dry') => {
    try {
      if(mode === 'dry'){
        const { data } = await api.post('/org-meta/purge', { dry_run: true });
        Modal.info({ title: '模拟同步报告', width: 800, content: <pre style={{ maxHeight:400, overflow:'auto' }}>{JSON.stringify(data.report, null, 2)}</pre> });
      } else if(mode === 'delta'){
        message.loading({ content: '正在执行增量同步...', key: 'orgSync' });
        const { data } = await api.post('/org-meta/purge', { rebuild: false });
        message.success({ content: '增量同步完成', key: 'orgSync' });
        console.log('org sync result', data);
      } else if(mode === 'rebuild'){
        Modal.confirm({
          title: '确认重建组织结构？',
          content: '重建将清空现有组织元数据并从在职员工数据重新生成，操作不可逆。',
          onOk: async () => {
            message.loading({ content: '正在重建...', key: 'orgSync' });
            const { data } = await api.post('/org-meta/purge', { rebuild: true });
            message.success({ content: '重建完成', key: 'orgSync' });
            console.log('org rebuild result', data);
          }
        });
      }
    } catch (e) {
      console.error('org sync error', e);
      message.error(e?.message || '同步失败');
    }
  };

  return (
    <div style={{ padding:24 }}>
      <h2 style={{ color:'#1f2937', borderBottom:'1px solid #e5e7eb', paddingBottom:16, marginBottom:24 }}>系统设置</h2>
      <Tabs
        destroyInactiveTabPane
        items={[
          { key:'general', label:'通用设置', children: generalSettingsContent },
          { key:'data', label:'数据设置', children: dataSettingsContent },
          { key:'org_hierarchy', label:'组织结构层级', children: (
            <div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                <Space>
                  <Button onClick={()=>handleOrgSync('dry')}>模拟同步（Dry run）</Button>
                  <Button type="primary" onClick={()=>handleOrgSync('delta')}>增量同步</Button>
                  <Button danger onClick={()=>handleOrgSync('rebuild')}>重建并覆盖</Button>
                </Space>
              </div>
              <OrganizationHierarchyManager />
            </div>
          ) }
        ]}
      />
    </div>
  );
};

export default Settings;


